// Изменено: 2025-12-24 - Создана утилита для конвертации валют на фронтенде
// Использует курс валюты локализации для конвертации цен
// Курс валюты передается через bootstrap data (currency_rate - курс валюты локализации к USD)

import {useBootstrapData} from '../core/bootstrap-data/bootstrap-data-context';

/**
 * Конвертировать цену из одной валюты в другую
 * 
 * @param amount Сумма для конвертации
 * @param fromCurrency Исходная валюта (например, 'USD')
 * @param toCurrency Целевая валюта (например, 'RUB')
 * @param currencyRate Курс целевой валюты к USD (если toCurrency не USD)
 * @returns Конвертированная сумма
 */
export function convertCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency: string,
  currencyRate?: number
): number {
  // Если валюты одинаковые, возвращаем исходную сумму
  if (fromCurrency === toCurrency) {
    return amount;
  }

  // Если целевая валюта USD, конвертируем из валюты локализации в USD
  if (toCurrency === 'USD' && fromCurrency !== 'USD') {
    // Нужен курс исходной валюты к USD, но у нас только курс валюты локализации
    // Если fromCurrency не валюта локализации, не можем конвертировать
    return amount;
  }

  // Если исходная валюта USD, конвертируем в валюту локализации
  if (fromCurrency === 'USD' && toCurrency !== 'USD') {
    if (!currencyRate || currencyRate === 0) {
      return amount;
    }
    // amount_in_target = amount_in_usd * currency_rate
    return Math.round(amount * currencyRate * 100) / 100;
  }

  // Если обе валюты не USD, не можем конвертировать (нужны курсы обеих валют)
  return amount;
}

/**
 * Хук для конвертации валют с использованием курсов из текущей локализации
 * 
 * @returns Функция для конвертации валют
 */
export function useCurrencyConverter() {
  const {
    data: {i18n},
  } = useBootstrapData();

  const currencyRate = i18n?.currency_rate as number | undefined;
  const localeCurrency = i18n?.currency as string | undefined;

  /**
   * Конвертировать цену в валюту текущей локализации
   * 
   * @param amount Сумма для конвертации
   * @param fromCurrency Исходная валюта (обычно USD)
   * @returns Объект с конвертированной суммой и валютой
   */
  const convertToLocaleCurrency = (
    amount: number,
    fromCurrency: string = 'USD'
  ): {amount: number; currency: string} => {
    const targetCurrency = localeCurrency || fromCurrency;

    if (fromCurrency === targetCurrency) {
      return {
        amount,
        currency: fromCurrency,
      };
    }

    // Конвертируем из USD в валюту локализации
    if (fromCurrency === 'USD' && currencyRate) {
      const convertedAmount = convertCurrency(
        amount,
        fromCurrency,
        targetCurrency,
        currencyRate
      );
      return {
        amount: convertedAmount,
        currency: targetCurrency,
      };
    }

    // Если не можем конвертировать, возвращаем исходную сумму
    return {
      amount,
      currency: fromCurrency,
    };
  };

  /**
   * Конвертировать цену из одной валюты в другую
   * Поддерживается конвертация из USD в валюту локализации
   * 
   * @param amount Сумма для конвертации
   * @param fromCurrency Исходная валюта
   * @param toCurrency Целевая валюта
   * @returns Конвертированная сумма
   */
  const convert = (
    amount: number,
    fromCurrency: string,
    toCurrency?: string
  ): number => {
    const targetCurrency = toCurrency || localeCurrency || fromCurrency;
    return convertCurrency(amount, fromCurrency, targetCurrency, currencyRate);
  };

  return {
    convert,
    convertToLocaleCurrency,
    currencyRate,
    localeCurrency,
  };
}

